import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  DollarSign,
  Plus,
  Check,
  Calendar,
  TrendingUp,
  TrendingDown,
  Receipt,
  Repeat,
  Loader2,
} from 'lucide-react';
import { GlassCard } from '@/components/ui/glass-card';
import { DataTable, type Column } from '@/components/ui/data-table';
import { Badge } from '@/components/ui/badge';
import { Modal } from '@/components/ui/modal';
import { apiFetch } from '@/lib/api';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';

// ─── TYPES ───────────────────────────────────────────────────────────────────

type FinancialTab = 'contas' | 'fluxo' | 'dre' | 'margem';

interface AccountPayable {
  id: string;
  description: string;
  creditor: string;
  amount: number;
  due_date: string;
  category: 'fixa' | 'variavel';
  status: 'pendente' | 'paga' | 'vencida';
  recurring: boolean;
}

interface AccountsPayableResponse {
  items: AccountPayable[];
  total: number;
  page: number;
  limit: number;
}

interface CashFlowData {
  date: string;
  sales_by_method: Record<string, number>;
  total_sales: number;
  sangrias: number;
  suprimentos: number;
  fiado_received: number;
  total_inflows: number;
  total_outflows: number;
  balance: number;
}

interface DREData {
  month: string;
  revenue: number;
  cmv: number;
  gross_profit: number;
  gross_margin_pct: number;
  expenses: number;
  net_profit: number;
  net_margin_pct: number;
  previous_month_revenue: number;
  revenue_variation_pct: number;
}

interface MarginItem {
  id: string;
  name: string;
  category: string;
  cost_price: number;
  sale_price: number;
  margin_value: number;
  margin_pct: number;
}

interface MarginResponse {
  items: MarginItem[];
  average_margin_pct: number;
  page: number;
  limit: number;
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
}

function getTodayStr(): string {
  return new Date().toISOString().split('T')[0]!;
}

function getCurrentMonthStr(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, 'default' | 'success' | 'destructive' | 'warning'> = {
    pendente: 'warning',
    paga: 'success',
    vencida: 'destructive',
  };
  const labels: Record<string, string> = {
    pendente: 'Pendente',
    paga: 'Paga',
    vencida: 'Vencida',
  };
  return <Badge variant={variants[status] || 'default'}>{labels[status] || status}</Badge>;
}

const METHOD_LABELS: Record<string, string> = {
  dinheiro: 'Dinheiro',
  cartao_debito: 'Cartão Débito',
  cartao_credito: 'Cartão Crédito',
  pix: 'PIX',
  fiado: 'Crédito',
  credito: 'Crédito',
};

// ─── API FUNCTIONS ───────────────────────────────────────────────────────────

async function fetchAccountsPayable(): Promise<AccountsPayableResponse> {
  const params = new URLSearchParams({ limit: '200' });
  const res = await apiFetch(`/api/financial/accounts-payable?${params.toString()}`);
  if (!res.ok) throw new Error('Erro ao carregar contas a pagar');
  return res.json();
}

async function createAccountPayable(data: {
  description: string;
  creditor: string;
  amount: number;
  due_date: string;
  category: 'fixa' | 'variavel';
  recurring: boolean;
}): Promise<AccountPayable> {
  const res = await apiFetch('/api/financial/accounts-payable', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('Erro ao criar conta');
  return res.json();
}

async function payAccount(id: string, paymentMethod: string): Promise<AccountPayable> {
  const res = await apiFetch(`/api/financial/accounts-payable/${id}`, {
    method: 'PATCH',
    body: JSON.stringify({
      status: 'paga',
      payment_date: new Date().toISOString().split('T')[0],
      payment_method: paymentMethod,
    }),
  });
  if (!res.ok) throw new Error('Erro ao pagar conta');
  return res.json();
}

async function fetchCashFlow(date: string): Promise<CashFlowData> {
  const res = await apiFetch(`/api/financial/cash-flow?date=${date}`);
  if (!res.ok) throw new Error('Erro ao carregar fluxo de caixa');
  return res.json();
}

async function fetchDRE(month: string): Promise<DREData> {
  const res = await apiFetch(`/api/financial/dre?month=${month}`);
  if (!res.ok) throw new Error('Erro ao carregar DRE');
  return res.json();
}

async function fetchMargin(): Promise<MarginResponse> {
  const res = await apiFetch('/api/financial/margin?limit=100');
  if (!res.ok) throw new Error('Erro ao carregar margens');
  return res.json();
}


// ─── CONTAS A PAGAR TAB ──────────────────────────────────────────────────────

function ContasAPagarTab() {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [showPayModal, setShowPayModal] = useState(false);
  const [payingAccount, setPayingAccount] = useState<AccountPayable | null>(null);
  const [paymentMethod, setPaymentMethod] = useState('dinheiro');
  const [filter, setFilter] = useState<string>('all');
  const [formData, setFormData] = useState({
    description: '',
    creditor: '',
    amount: '',
    due_date: '',
    category: 'variavel' as 'fixa' | 'variavel',
    recurring: false,
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['accounts-payable'],
    queryFn: () => fetchAccountsPayable(),
  });

  const createMutation = useMutation({
    mutationFn: createAccountPayable,
    onSuccess: () => {
      toast.success('Conta criada com sucesso');
      queryClient.invalidateQueries({ queryKey: ['accounts-payable'] });
      setShowModal(false);
      setFormData({ description: '', creditor: '', amount: '', due_date: '', category: 'variavel', recurring: false });
    },
    onError: () => {
      toast.error('Erro ao criar conta');
    },
  });

  const payMutation = useMutation({
    mutationFn: ({ id, method }: { id: string; method: string }) => payAccount(id, method),
    onSuccess: () => {
      toast.success('Conta marcada como paga');
      queryClient.invalidateQueries({ queryKey: ['accounts-payable'] });
      setShowPayModal(false);
      setPayingAccount(null);
    },
    onError: () => {
      toast.error('Erro ao pagar conta');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.description || !formData.creditor || !formData.amount || !formData.due_date) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }
    createMutation.mutate({
      description: formData.description,
      creditor: formData.creditor,
      amount: Number(formData.amount),
      due_date: formData.due_date,
      category: formData.category,
      recurring: formData.recurring,
    });
  };

  function handlePayClick(account: AccountPayable) {
    setPayingAccount(account);
    setPaymentMethod('dinheiro');
    setShowPayModal(true);
  }

  function handleConfirmPay() {
    if (!payingAccount) return;
    payMutation.mutate({ id: payingAccount.id, method: paymentMethod });
  }

  if (error) {
    toast.error('Erro ao carregar contas a pagar');
  }

  const accounts = data?.items || [];
  const filteredAccounts = filter === 'all'
    ? accounts
    : accounts.filter((a) => a.status === filter);

  const totalPendente = accounts
    .filter((a) => a.status === 'pendente')
    .reduce((sum, a) => sum + Number(a.amount), 0);
  const totalVencida = accounts
    .filter((a) => a.status === 'vencida')
    .reduce((sum, a) => sum + Number(a.amount), 0);
  const totalPaga = accounts
    .filter((a) => a.status === 'paga')
    .reduce((sum, a) => sum + Number(a.amount), 0);

  const columns: Column<AccountPayable>[] = [
    { key: 'description', header: 'Descrição', sortable: true },
    { key: 'creditor', header: 'Credor', sortable: true },
    {
      key: 'amount',
      header: 'Valor',
      sortable: true,
      render: (row) => <span className="font-medium text-base">{formatCurrency(Number(row.amount))}</span>,
    },
    {
      key: 'due_date',
      header: 'Vencimento',
      sortable: true,
      render: (row) => <span className="text-base">{formatDate(row.due_date)}</span>,
    },
    {
      key: 'category',
      header: 'Categoria',
      render: (row) => (
        <Badge variant="default">{row.category === 'fixa' ? 'Fixa' : 'Variável'}</Badge>
      ),
    },
    {
      key: 'recurring',
      header: 'Recorrente',
      render: (row) => row.recurring ? <Repeat className="h-4 w-4 text-primary" /> : <span className="text-muted-foreground">—</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (row) =>
        row.status === 'pendente' || row.status === 'vencida' ? (
          <button
            onClick={() => handlePayClick(row)}
            className="rounded-md bg-green-600/20 px-3 py-1.5 text-sm text-green-400 hover:bg-green-600/30 transition-colors"
          >
            <Check className="inline h-3.5 w-3.5 mr-1" />
            Pagar
          </button>
        ) : null,
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Cards resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-yellow-500/20 p-2">
              <Calendar className="h-5 w-5 text-yellow-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Pendente</p>
              <p className="text-xl font-bold text-foreground">{formatCurrency(totalPendente)}</p>
            </div>
          </div>
        </GlassCard>
        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-red-500/20 p-2">
              <DollarSign className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Vencido</p>
              <p className="text-xl font-bold text-red-400">{formatCurrency(totalVencida)}</p>
            </div>
          </div>
        </GlassCard>
        <GlassCard className="p-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-green-500/20 p-2">
              <Check className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Pago (mês)</p>
              <p className="text-xl font-bold text-green-400">{formatCurrency(totalPaga)}</p>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Filtros e ações */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          {[
            { key: 'all', label: 'Todas' },
            { key: 'pendente', label: 'Pendentes' },
            { key: 'vencida', label: 'Vencidas' },
            { key: 'paga', label: 'Pagas' },
          ].map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`rounded-lg px-3 py-2 text-base transition-colors ${
                filter === f.key
                  ? 'bg-primary text-white'
                  : 'bg-card text-muted-foreground hover:bg-accent'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-base font-medium text-white hover:bg-primary/90 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Nova Conta
        </button>
      </div>

      {/* Tabela */}
      <DataTable
        columns={columns}
        data={filteredAccounts}
        getRowKey={(row) => row.id}
        emptyMessage="Nenhuma conta encontrada"
      />

      {/* Modal Criar Conta */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title="Nova Conta a Pagar">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-muted-foreground mb-1">Descrição *</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Ex: Aluguel"
            />
          </div>
          <div>
            <label className="block text-sm text-muted-foreground mb-1">Credor *</label>
            <input
              type="text"
              value={formData.creditor}
              onChange={(e) => setFormData({ ...formData, creditor: e.target.value })}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Ex: Imobiliária XYZ"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Valor (R$) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="0,00"
              />
            </div>
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Vencimento *</label>
              <input
                type="date"
                value={formData.due_date}
                onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-muted-foreground mb-1">Categoria</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value as 'fixa' | 'variavel' })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="variavel">Variável</option>
                <option value="fixa">Fixa</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.recurring}
                  onChange={(e) => setFormData({ ...formData, recurring: e.target.checked })}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
                <span className="text-base text-muted-foreground">Recorrente</span>
              </label>
            </div>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setShowModal(false)}
              className="rounded-lg px-4 py-2.5 text-base text-muted-foreground hover:bg-accent transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-base font-medium text-white hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Salvar
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal Pagar Conta */}
      <Modal open={showPayModal} onClose={() => setShowPayModal(false)} title="Confirmar Pagamento">
        <div className="space-y-4">
          {payingAccount && (
            <div className="rounded-lg border border-border bg-muted/30 p-4">
              <p className="text-base font-medium text-foreground">{payingAccount.description}</p>
              <p className="text-sm text-muted-foreground mt-1">Credor: {payingAccount.creditor}</p>
              <p className="text-xl font-bold text-foreground mt-2">{formatCurrency(Number(payingAccount.amount))}</p>
              <p className="text-sm text-muted-foreground">Vencimento: {formatDate(payingAccount.due_date)}</p>
            </div>
          )}
          <div>
            <label className="block text-sm text-muted-foreground mb-1">Forma de Pagamento</label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="dinheiro">Dinheiro</option>
              <option value="pix">PIX</option>
              <option value="cartao_debito">Cartão Débito</option>
              <option value="cartao_credito">Cartão Crédito</option>
              <option value="transferencia">Transferência</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setShowPayModal(false)}
              className="rounded-lg px-4 py-2.5 text-base text-muted-foreground hover:bg-accent transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleConfirmPay}
              disabled={payMutation.isPending}
              className="flex items-center gap-2 rounded-lg bg-green-600 px-4 py-2.5 text-base font-medium text-white hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {payMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              <Check className="h-4 w-4" />
              Confirmar Pagamento
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}


// ─── FLUXO DE CAIXA TAB ─────────────────────────────────────────────────────

function FluxoDeCaixaTab() {
  const [selectedDate, setSelectedDate] = useState(getTodayStr());

  const { data: flow, isLoading, error } = useQuery({
    queryKey: ['cash-flow', selectedDate],
    queryFn: () => fetchCashFlow(selectedDate),
  });

  if (error) {
    toast.error('Erro ao carregar fluxo de caixa');
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!flow) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <label className="text-base text-muted-foreground">Data:</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="rounded-lg border border-border bg-card px-3 py-2.5 text-base text-foreground"
          />
        </div>
        <div className="text-center py-12 text-base text-muted-foreground">
          Nenhum dado de fluxo de caixa para esta data.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Seletor de data */}
      <div className="flex items-center gap-4">
        <label className="text-base text-muted-foreground">Data:</label>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="rounded-lg border border-border bg-card px-3 py-2.5 text-base text-foreground"
        />
      </div>

      {/* Cards resumo */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Total Entradas</p>
          <p className="text-xl font-bold text-green-400">{formatCurrency(flow.total_inflows)}</p>
        </GlassCard>
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Total Saídas</p>
          <p className="text-xl font-bold text-red-400">{formatCurrency(flow.total_outflows)}</p>
        </GlassCard>
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Saldo do Dia</p>
          <p className={`text-xl font-bold ${flow.balance >= 0 ? 'text-foreground' : 'text-red-400'}`}>
            {formatCurrency(flow.balance)}
          </p>
        </GlassCard>
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Crédito Recebido</p>
          <p className="text-xl font-bold text-cyan-400">{formatCurrency(flow.fiado_received)}</p>
        </GlassCard>
      </div>

      {/* Detalhamento */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <GlassCard>
          <h3 className="mb-4 text-base font-semibold text-foreground flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-green-400" />
            Entradas
          </h3>
          <div className="space-y-3">
            {Object.entries(flow.sales_by_method).map(([method, value]) => (
              <div key={method} className="flex items-center justify-between">
                <span className="text-base text-muted-foreground">{METHOD_LABELS[method] || method}</span>
                <span className="text-base font-medium text-foreground">{formatCurrency(value)}</span>
              </div>
            ))}
            {flow.suprimentos > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-base text-muted-foreground">Suprimentos</span>
                <span className="text-base font-medium text-foreground">{formatCurrency(flow.suprimentos)}</span>
              </div>
            )}
            {flow.fiado_received > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-base text-muted-foreground">Crédito Recebido</span>
                <span className="text-base font-medium text-foreground">{formatCurrency(flow.fiado_received)}</span>
              </div>
            )}
            <div className="border-t border-border pt-2 flex items-center justify-between">
              <span className="text-base font-semibold text-foreground">Total</span>
              <span className="text-base font-bold text-green-400">{formatCurrency(flow.total_inflows)}</span>
            </div>
          </div>
        </GlassCard>

        <GlassCard>
          <h3 className="mb-4 text-base font-semibold text-foreground flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-red-400" />
            Saídas
          </h3>
          <div className="space-y-3">
            {flow.sangrias > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-base text-muted-foreground">Sangrias</span>
                <span className="text-base font-medium text-foreground">{formatCurrency(flow.sangrias)}</span>
              </div>
            )}
            {flow.total_outflows === 0 && (
              <p className="text-base text-muted-foreground">Nenhuma saída registrada</p>
            )}
            <div className="border-t border-border pt-2 flex items-center justify-between">
              <span className="text-base font-semibold text-foreground">Total</span>
              <span className="text-base font-bold text-red-400">{formatCurrency(flow.total_outflows)}</span>
            </div>
          </div>
        </GlassCard>
      </div>
    </div>
  );
}

// ─── DRE TAB ─────────────────────────────────────────────────────────────────

function DRETab() {
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthStr());

  const { data: dre, isLoading, error } = useQuery({
    queryKey: ['dre', selectedMonth],
    queryFn: () => fetchDRE(selectedMonth),
  });

  const getLastSixMonths = useCallback((): string[] => {
    const months: string[] = [];
    const [yearStr, monthStr] = selectedMonth.split('-');
    let year = Number(yearStr);
    let month = Number(monthStr);
    for (let i = 5; i >= 0; i--) {
      let m = month - i;
      let y = year;
      while (m <= 0) {
        m += 12;
        y -= 1;
      }
      months.push(`${y}-${String(m).padStart(2, '0')}`);
    }
    return months;
  }, [selectedMonth]);

  const sixMonths = getLastSixMonths();

  const { data: trendData } = useQuery({
    queryKey: ['dre-trend', selectedMonth],
    queryFn: async () => {
      const results = await Promise.all(
        sixMonths.map(async (m) => {
          try {
            const data = await fetchDRE(m);
            return data;
          } catch {
            return { month: m, revenue: 0, net_profit: 0 } as DREData;
          }
        })
      );
      return results;
    },
  });

  if (error) {
    toast.error('Erro ao carregar DRE');
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!dre) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <label className="text-base text-muted-foreground">Mês:</label>
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="rounded-lg border border-border bg-card px-3 py-2.5 text-base text-foreground"
          />
        </div>
        <div className="text-center py-12 text-base text-muted-foreground">
          Nenhum dado de DRE para este mês.
        </div>
      </div>
    );
  }

  const chartData = (trendData || []).map((d) => {
    const [, m] = d.month.split('-');
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
    return {
      name: monthNames[Number(m) - 1] || m,
      receita: d.revenue,
      lucro: d.net_profit,
    };
  });

  return (
    <div className="space-y-6">
      {/* Seletor de mês */}
      <div className="flex items-center gap-4">
        <label className="text-base text-muted-foreground">Mês:</label>
        <input
          type="month"
          value={selectedMonth}
          onChange={(e) => setSelectedMonth(e.target.value)}
          className="rounded-lg border border-border bg-card px-3 py-2.5 text-base text-foreground"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* DRE Simplificado */}
        <GlassCard>
          <h3 className="mb-4 text-base font-semibold text-foreground flex items-center gap-2">
            <Receipt className="h-4 w-4 text-primary" />
            DRE Simplificado — {dre.month}
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-base text-muted-foreground">Receita Bruta</span>
              <span className="text-base font-medium text-foreground">{formatCurrency(dre.revenue)}</span>
            </div>
            <div className="flex items-center justify-between text-red-400">
              <span className="text-base">(-) CMV</span>
              <span className="text-base font-medium">{formatCurrency(dre.cmv)}</span>
            </div>
            <div className="border-t border-border pt-2 flex items-center justify-between">
              <span className="text-base font-semibold text-foreground">(=) Lucro Bruto</span>
              <span className="text-base font-bold text-foreground">
                {formatCurrency(dre.gross_profit)}
                <span className="ml-2 text-sm text-muted-foreground">({dre.gross_margin_pct.toFixed(1)}%)</span>
              </span>
            </div>
            <div className="flex items-center justify-between text-red-400">
              <span className="text-base">(-) Despesas Operacionais</span>
              <span className="text-base font-medium">{formatCurrency(dre.expenses)}</span>
            </div>
            <div className="border-t-2 border-primary/30 pt-3 flex items-center justify-between">
              <span className="text-lg font-bold text-foreground">(=) Lucro Líquido</span>
              <span className={`text-lg font-bold ${dre.net_profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatCurrency(dre.net_profit)}
                <span className="ml-2 text-sm text-muted-foreground">({dre.net_margin_pct.toFixed(1)}%)</span>
              </span>
            </div>
          </div>

          {/* Variação */}
          <div className="mt-4 rounded-lg bg-accent/30 p-3">
            <div className="flex items-center gap-2">
              {dre.revenue_variation_pct >= 0 ? (
                <TrendingUp className="h-4 w-4 text-green-400" />
              ) : (
                <TrendingDown className="h-4 w-4 text-red-400" />
              )}
              <span className="text-base text-muted-foreground">vs mês anterior:</span>
              <span className={`text-base font-medium ${dre.revenue_variation_pct >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {dre.revenue_variation_pct >= 0 ? '+' : ''}{dre.revenue_variation_pct.toFixed(1)}%
              </span>
            </div>
          </div>
        </GlassCard>

        {/* Gráfico */}
        <GlassCard>
          <h3 className="mb-4 text-base font-semibold text-foreground">Evolução (6 meses)</h3>
          {chartData.length > 0 ? (
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={chartData}>
                <defs>
                  <linearGradient id="colorReceita" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorLucro" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#00B894" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#00B894" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#2D2D3D" />
                <XAxis dataKey="name" stroke="#A0A0B0" fontSize={12} />
                <YAxis stroke="#A0A0B0" fontSize={12} tickFormatter={(v: number) => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1A1A24', border: '1px solid #2D2D3D', borderRadius: '8px' }}
                  labelStyle={{ color: '#FFFFFF' }}
                  formatter={(value: number) => formatCurrency(value)}
                />
                <Area type="monotone" dataKey="receita" stroke="#10B981" fill="url(#colorReceita)" name="Receita" />
                <Area type="monotone" dataKey="lucro" stroke="#00B894" fill="url(#colorLucro)" name="Lucro" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-[250px] text-muted-foreground text-base">
              Sem dados para exibir gráfico
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}


// ─── MARGEM DE LUCRO TAB ─────────────────────────────────────────────────────

function MargemTab() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['margin'],
    queryFn: fetchMargin,
  });

  if (error) {
    toast.error('Erro ao carregar margens de lucro');
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const items = data?.items || [];
  const avgMargin = data?.average_margin_pct || 0;

  const columns: Column<MarginItem>[] = [
    { key: 'name', header: 'Produto', sortable: true },
    { key: 'category', header: 'Categoria', sortable: true },
    {
      key: 'cost_price',
      header: 'Preço Custo',
      sortable: true,
      render: (row) => <span className="text-base">{formatCurrency(row.cost_price)}</span>,
    },
    {
      key: 'sale_price',
      header: 'Preço Venda',
      sortable: true,
      render: (row) => <span className="text-base">{formatCurrency(row.sale_price)}</span>,
    },
    {
      key: 'margin_value',
      header: 'Margem (R$)',
      sortable: true,
      render: (row) => <span className="text-green-400 font-medium">{formatCurrency(row.margin_value)}</span>,
    },
    {
      key: 'margin_pct',
      header: 'Margem (%)',
      sortable: true,
      render: (row) => (
        <Badge variant={row.margin_pct >= 50 ? 'success' : row.margin_pct >= 30 ? 'warning' : 'destructive'}>
          {row.margin_pct.toFixed(1)}%
        </Badge>
      ),
    },
  ];

  const chartData = items.slice(0, 10).map((m) => ({
    name: m.name.length > 12 ? m.name.substring(0, 12) + '...' : m.name,
    margem: Number(m.margin_pct.toFixed(1)),
  }));

  return (
    <div className="space-y-6">
      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Margem Média</p>
          <p className="text-2xl font-bold text-foreground">{avgMargin.toFixed(1)}%</p>
        </GlassCard>
        <GlassCard className="p-4">
          <p className="text-sm text-muted-foreground">Produtos Analisados</p>
          <p className="text-2xl font-bold text-foreground">{items.length}</p>
        </GlassCard>
      </div>

      {/* Gráfico */}
      {chartData.length > 0 && (
        <GlassCard>
          <h3 className="mb-4 text-base font-semibold text-foreground">Top 10 Margens por Produto (%)</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2D2D3D" />
              <XAxis dataKey="name" stroke="#A0A0B0" fontSize={11} />
              <YAxis stroke="#A0A0B0" fontSize={12} />
              <Tooltip
                contentStyle={{ backgroundColor: '#1A1A24', border: '1px solid #2D2D3D', borderRadius: '8px' }}
                labelStyle={{ color: '#FFFFFF' }}
                formatter={(value: number) => `${value.toFixed(1)}%`}
              />
              <Bar dataKey="margem" fill="#10B981" radius={[4, 4, 0, 0]} name="Margem %" />
            </BarChart>
          </ResponsiveContainer>
        </GlassCard>
      )}

      {/* Tabela */}
      <DataTable
        columns={columns}
        data={items}
        getRowKey={(row) => row.id}
        emptyMessage="Nenhum produto encontrado"
      />
    </div>
  );
}

// ─── MAIN PAGE ───────────────────────────────────────────────────────────────

const tabs: { id: FinancialTab; label: string; icon: React.ReactNode }[] = [
  { id: 'contas', label: 'Contas a Pagar', icon: <DollarSign className="h-4 w-4" /> },
  { id: 'fluxo', label: 'Fluxo de Caixa', icon: <TrendingUp className="h-4 w-4" /> },
  { id: 'dre', label: 'DRE', icon: <Receipt className="h-4 w-4" /> },
  { id: 'margem', label: 'Margem de Lucro', icon: <TrendingUp className="h-4 w-4" /> },
];

export function FinancialPage() {
  const [activeTab, setActiveTab] = useState<FinancialTab>('contas');

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-foreground">Financeiro</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg bg-card p-1 border border-border overflow-x-auto">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 rounded-md px-4 py-2.5 text-base font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-primary text-white shadow-sm'
                : 'text-muted-foreground hover:text-foreground hover:bg-accent'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Conteúdo da aba */}
      {activeTab === 'contas' && <ContasAPagarTab />}
      {activeTab === 'fluxo' && <FluxoDeCaixaTab />}
      {activeTab === 'dre' && <DRETab />}
      {activeTab === 'margem' && <MargemTab />}
    </div>
  );
}
